/**
 * Stable DOM ids for every prescription card. Used by:
 *   • ModCard's `id` prop - anchors each card in the DOM.
 *   • The summary helper - returned on each ModEntry so the prescription
 *     assistant's "Currently selected" chips can scroll to the right card.
 *
 * Keep these in sync with the actual ModCard / DualColumnCard render sites.
 * They're plain string constants instead of an enum so they serialize
 * cleanly through React props + analytics events without coercion.
 */

export const CARD_IDS = {
  // Insole design
  topCover: 'card-top-cover',
  // Rearfoot
  subcalcanealOffloading: 'card-subcalcaneal-offloading',
  heelRaise: 'card-heel-raise',
  rearfootPosting: 'card-rearfoot-posting',
  rearfootWedge: 'card-rearfoot-wedge',
  // Midfoot
  archHeightAdjustment: 'card-arch-height-adjustment',
  fascialAccommodation: 'card-fascial-accommodation',
  mlaExtraFilled: 'card-mla-extra-filled',
  medialFlange: 'card-medial-flange',
  lateralFlange: 'card-lateral-flange',
  archReinforcement: 'card-arch-reinforcement',
  // Forefoot
  forefootWedge: 'card-forefoot-wedge',
  gaitPlate: 'card-gait-plate',
  mortonExtension: 'card-morton-extension',
  firstRayCutOut: 'card-first-ray-cut-out',
  metatarsalExtension: 'card-metatarsal-extension',
  // Offloadings
  metatarsalCutOuts: 'card-metatarsal-cut-outs',
  kineticWedge: 'card-kinetic-wedge',
  subcapitalBar: 'card-subcapital-bar',
  dropMetPad: 'card-drop-met-pad',
  mttBar: 'card-mtt-bar',
  metatarsalHeadCutOuts: 'card-metatarsal-head-cut-outs',
  extraHeelCushionPad: 'card-extra-heel-cushion-pad',
  /** Sandal-only (R37). */
  toePulpOffloading: 'card-toe-pulp-offloading',
} as const;

export type CardId = (typeof CARD_IDS)[keyof typeof CARD_IDS];

/** The prescription form is split into a handful of collapsible sections.
 *  When the assistant wants to scroll to a specific card we also need to
 *  force-open its parent section, otherwise the card hasn't rendered yet.
 *
 *  Patient + Top cover live inside the Insole design + Patient sections
 *  respectively. Modification cards group into rearfoot / midfoot /
 *  forefoot / offloadings. */
export type SectionKey =
  | 'patient'
  | 'design'
  | 'rearfoot'
  | 'midfoot'
  | 'forefoot'
  | 'offloadings'
  | 'sandal';

export const SECTION_OF_CARD: Record<string, SectionKey> = {
  [CARD_IDS.topCover]: 'design',
  [CARD_IDS.subcalcanealOffloading]: 'rearfoot',
  [CARD_IDS.heelRaise]: 'rearfoot',
  [CARD_IDS.rearfootPosting]: 'rearfoot',
  [CARD_IDS.rearfootWedge]: 'rearfoot',
  [CARD_IDS.archHeightAdjustment]: 'midfoot',
  [CARD_IDS.fascialAccommodation]: 'midfoot',
  [CARD_IDS.mlaExtraFilled]: 'midfoot',
  [CARD_IDS.medialFlange]: 'midfoot',
  [CARD_IDS.lateralFlange]: 'midfoot',
  [CARD_IDS.archReinforcement]: 'midfoot',
  [CARD_IDS.forefootWedge]: 'forefoot',
  [CARD_IDS.gaitPlate]: 'forefoot',
  [CARD_IDS.mortonExtension]: 'forefoot',
  [CARD_IDS.firstRayCutOut]: 'forefoot',
  [CARD_IDS.metatarsalExtension]: 'forefoot',
  [CARD_IDS.metatarsalCutOuts]: 'offloadings',
  [CARD_IDS.kineticWedge]: 'offloadings',
  [CARD_IDS.subcapitalBar]: 'offloadings',
  [CARD_IDS.dropMetPad]: 'offloadings',
  [CARD_IDS.mttBar]: 'offloadings',
  [CARD_IDS.metatarsalHeadCutOuts]: 'offloadings',
  [CARD_IDS.extraHeelCushionPad]: 'offloadings',
  [CARD_IDS.toePulpOffloading]: 'offloadings',
};
